import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import clientService, { CreateClientData, UpdateClientData, Client } from '../../services/clientService';
import { useClientLimits } from '../../hooks/useClientLimits';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Plus, Building, Globe, Mail, Phone, Image as ImageIcon, Link as LinkIcon, Loader2, AlertCircle, Crown } from 'lucide-react';
import { Button } from '../ui/button';
import toast from 'react-hot-toast';

interface ClientFormData extends CreateClientData {
  logo_url?: string;
}

interface ClientFormProps {
  mode: 'create' | 'edit';
}

const InputField = ({ name, label, required, register, errors, placeholder, type = 'text', disabled = false, icon: Icon }) => (
  <div>
    <label className="block text-sm font-bold text-slate-900 dark:text-slate-300 mb-2">{label}{required && ' *'}</label>
    <div className="relative">
      <div className="absolute left-4 top-1/2 -translate-y-1/2">
        <Icon className="w-5 h-5 text-gray-500 dark:text-slate-400" />
      </div>
      <input
        {...register(name, { required: required && `${label} is required` })}
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-slate-800/50 border border-gray-300 dark:border-slate-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all duration-300 disabled:bg-gray-100 dark:disabled:bg-slate-800/30 disabled:cursor-not-allowed text-black dark:text-white placeholder-gray-500 dark:placeholder-slate-400 ${errors[name] ? 'border-red-500 dark:border-red-500/50' : ''}`}
      />
    </div>
    {errors[name] && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors[name].message}</p>}
  </div>
);

const ClientForm: React.FC<ClientFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();
  const { canCreateClient, currentCount, maxLimit, isUnlimited, isBetaTester } = useClientLimits();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingClient, setIsLoadingClient] = useState(mode === 'edit');
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ClientFormData>();

  const watchedName = watch('name', '');

  // Check limit when in create mode
  useEffect(() => {
    if (mode === 'create' && !canCreateClient) {
      toast.error(
        `Brand limit reached (${currentCount}/${maxLimit}). Upgrade your plan to create more brands.`,
        {
          duration: 5000,
          icon: <AlertCircle className="w-5 h-5 text-amber-500" />,
        }
      );
      // Redirect back to clients list after a short delay
      const timer = setTimeout(() => {
        navigate('/app/clients');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [mode, canCreateClient, currentCount, maxLimit, navigate]);

  useEffect(() => {
    if (mode === 'edit' && clientId) {
      loadClient();
    }
  }, [mode, clientId]);

  useEffect(() => {
    if (mode === 'create' && watchedName) {
      const slug = watchedName.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').trim();
      setValue('slug', slug);
    }
  }, [watchedName, mode, setValue]);

  const loadClient = async () => {
    if (!clientId) return;
    try {
      setIsLoadingClient(true);
      const clientData = await clientService.getClient(clientId);
      Object.keys(clientData).forEach(key => {
        setValue(key as keyof ClientFormData, clientData[key]);
      });
    } catch (err: any) {
      setError('Failed to load brand data');
    } finally {
      setIsLoadingClient(false);
    }
  };

  const onSubmit = async (data: ClientFormData) => {
    setIsLoading(true);
    setError(null);
    try {
      if (mode === 'create') {
        const newClient = await clientService.createClient(data);
        if ((window as any).refreshClientSelector) (window as any).refreshClientSelector();
        toast.success('Brand created successfully!');
        navigate(`/app/clients/${newClient.id}`);
      } else if (mode === 'edit' && clientId) {
        const { slug, ...updateData } = data;
        await clientService.updateClient(clientId, updateData);
        toast.success('Brand updated successfully!');
        navigate(`/app/clients/${clientId}`);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || `Failed to ${mode} brand`;
      
      // Check if it's a limit error
      if (errorMessage.includes('limit reached') || errorMessage.includes('Brand/client limit')) {
        toast.error(errorMessage, {
          duration: 5000,
          icon: <AlertCircle className="w-5 h-5 text-amber-500" />,
        });
        // Redirect to clients page after showing error
        setTimeout(() => {
          navigate('/app/clients');
        }, 2000);
      } else {
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingClient) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900">
        <div className="w-16 h-16 border-4 border-gray-300 dark:border-slate-600/30 border-t-indigo-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Show limit reached message if in create mode and at limit
  if (mode === 'create' && !canCreateClient) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 text-center"
        >
          <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Brand Limit Reached</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You've reached your limit of <span className="font-bold text-gray-900 dark:text-white">{maxLimit} brand{maxLimit !== 1 ? 's' : ''}</span>.
          </p>
          <div className="bg-gray-100 dark:bg-slate-700/50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Current usage:</span>
              <span className="text-2xl font-bold">{currentCount}/{maxLimit}</span>
            </div>
          </div>
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/pricing')}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              Upgrade Plan
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/app/clients')}
              className="w-full"
            >
              Back to Brands
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white">
      {/* Floating Header */}
      <div className="backdrop-blur-2xl bg-white/80 dark:bg-slate-900/60 border-b border-gray-200/50 dark:border-slate-700/30">
        <div className="px-8 py-6">
          <div className="flex items-center justify-between">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <div className="flex items-center space-x-4">
                <Link to={clientId ? `/app/clients/${clientId}` : '/app/clients'}>
                  <motion.button whileHover={{ scale: 1.1, x: -2 }} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-slate-800/50 dark:hover:bg-slate-700/70 border border-gray-300/50 dark:border-slate-600/50">
                    <ArrowLeft className="w-6 h-6 text-gray-600 dark:text-slate-300" />
                  </motion.button>
                </Link>
                <div>
                  <h1 className="text-4xl font-black bg-gradient-to-r from-gray-900 via-indigo-600 to-purple-600 dark:from-white dark:via-indigo-200 dark:to-purple-200 bg-clip-text text-transparent">
                    {mode === 'create' ? 'Create Brand' : 'Edit Brand'}
                  </h1>
                  <p className="text-gray-600 dark:text-slate-300 text-lg">Manage brand details and settings</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-8 py-8">
        <motion.div 
          className="max-w-4xl mx-auto bg-white/80 dark:bg-slate-800/60 backdrop-blur-xl border border-gray-200/50 dark:border-slate-700/50 rounded-3xl p-8 shadow-xl"
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
        >
          {error && (
            <div className="mb-6 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl p-4">
              <p className="text-red-600 dark:text-red-400 font-bold">{error}</p>
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <InputField name="name" label="Brand Name" required register={register} errors={errors} placeholder="Acme Inc." icon={Building} />
              <InputField name="slug" label="Slug" required register={register} errors={errors} placeholder="acme-inc" disabled={mode === 'edit'} icon={LinkIcon} />
            </div>
            
            <div>
              <label className="block text-sm font-bold text-slate-900 dark:text-slate-300 mb-2">Description</label>
              <textarea
                {...register('description')}
                rows={4}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800/50 border border-gray-300 dark:border-slate-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all duration-300 text-black dark:text-white placeholder-gray-500 dark:placeholder-slate-400"
                placeholder="Describe your brand and business..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <InputField name="website" label="Website" register={register} errors={errors} placeholder="https://acme.com" type="url" icon={Globe} />
              <InputField name="email" label="Contact Email" register={register} errors={errors} placeholder="contact@acme.com" type="email" icon={Mail} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <InputField name="phone" label="Phone Number" register={register} errors={errors} placeholder="+1 234 567 890" type="tel" icon={Phone} />
              {/* <InputField name="logo_url" label="Logo URL" register={register} errors={errors} placeholder="https://acme.com/logo.png" type="url" icon={ImageIcon} /> */}
            </div>

            <div className="flex justify-end space-x-4 pt-8 border-t border-gray-200 dark:border-slate-700/50">
              <motion.button
                type="button"
                onClick={() => navigate(clientId ? `/app/clients/${clientId}` : '/app/clients')}
                className="px-8 py-3 bg-gray-500 dark:bg-slate-700/50 text-white dark:text-slate-200 font-bold rounded-xl border border-gray-300 dark:border-slate-600/50 hover:bg-gray-600 dark:hover:bg-slate-700 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
              >
                Cancel
              </motion.button>
              <motion.button
                type="submit"
                disabled={isLoading}
                className="flex items-center justify-center px-8 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-bold rounded-xl shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 transition-all duration-300 disabled:opacity-70"
                whileHover={{ scale: 1.05 }}
              >
                {isLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : (mode === 'create' ? <Plus className="w-6 h-6 mr-2" /> : <Save className="w-6 h-6 mr-2" />)}
                {isLoading ? 'Processing...' : (mode === 'create' ? 'Create Brand' : 'Save Changes')}
              </motion.button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default ClientForm;
